/**
 * A card with a header section (TMMAPP-0102, visual contract §6): padding 16 20 over a `line` rule,
 * the title in `h3` type (its level stays `heading`), the sub-line `caption` in `text2`, then the
 * body at padding 16 20. The app's own blocks that the prototype doesn't draw (D1) sit in this card.
 */
import type { ReactNode, Ref } from 'react';
import { Box, Paper, Stack, Typography, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { resolveTokens } from '@/theme/theme';

interface HeadedCardProps {
  title: ReactNode;
  heading?: 'h2' | 'h3';
  subtitle?: ReactNode;
  /** Controls at the header's inline end. */
  actions?: ReactNode;
  children: ReactNode;
  sx?: SxProps<Theme>;
  /** The card's anchor (a session section a tab jumps to). */
  id?: string;
  /** A group's name and state, where the block is a labelled region of its own. */
  role?: 'group';
  'aria-label'?: string;
  'aria-busy'?: boolean;
  /** The title, focusable from script, where an action inside the card can leave focus nowhere. */
  headingRef?: Ref<HTMLHeadingElement>;
}

export function HeadedCard({
  title,
  heading = 'h2',
  subtitle,
  actions,
  children,
  sx,
  id,
  role,
  'aria-label': ariaLabel,
  'aria-busy': ariaBusy,
  headingRef,
}: HeadedCardProps) {
  const tokens = resolveTokens(useTheme());
  const has = (node: ReactNode) => node !== undefined && node !== null && node !== '';
  return (
    <Paper
      component="section"
      id={id}
      role={role}
      aria-label={ariaLabel}
      aria-busy={ariaBusy}
      sx={[{ p: 0, overflow: 'hidden' }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        data-card-header=""
        sx={{
          alignItems: { sm: 'center' },
          justifyContent: 'space-between',
          gap: 1,
          p: '16px 20px',
          borderBottom: `1px solid ${tokens.line}`,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h3"
            component={heading}
            {...(headingRef !== undefined ? { ref: headingRef, tabIndex: -1 } : {})}
          >
            {title}
          </Typography>
          {has(subtitle) && (
            <Typography variant="caption" component="p" sx={{ color: tokens.text2 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {has(actions) && actions}
      </Stack>
      <Box sx={{ p: '16px 20px' }}>{children}</Box>
    </Paper>
  );
}
